import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type SummaryRangeOption =
  | 'Today'
  | 'Yesterday'
  | 'Weekly'
  | 'Monthly'
  | 'Yearly';

export type SummaryState = {
  selectedRange: SummaryRangeOption;
  loading: boolean;
  error: string | null;
  data: {
    duePaymentsAmount: number;
    lowStocksCount: number;
    expensesAmount: number;
    stockSellValueAmount: number;
    profitAmount: number;
    completedOrdersCount: number;
    pendingOrdersCount: number;
    partialOrdersCount: number;
    staffCount: number;
  } | null;
  cache: Partial<
    Record<
      SummaryRangeOption,
      {
        duePaymentsAmount: number;
        lowStocksCount: number;
        expensesAmount: number;
        stockSellValueAmount: number;
        profitAmount: number;
        completedOrdersCount: number;
        pendingOrdersCount: number;
        partialOrdersCount: number;
        staffCount: number;
      }
    >
  >;
};

const initialState: SummaryState = {
  selectedRange: 'Today',
  loading: false,
  error: null,
  data: null,
  cache: {},
};

const summarySlice = createSlice({
  name: 'backofficeSummary',
  initialState,
  reducers: {
    setSummaryRange(state, action: PayloadAction<SummaryRangeOption>) {
      state.selectedRange = action.payload;
      // Hydrate current view from cache if available for the selected range
      const cached = state.cache?.[action.payload];
      if (cached) {
        state.data = cached;
      }
    },
    setSummaryLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSummaryError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setSummaryData(
      state,
      action: PayloadAction<{
        range: SummaryRangeOption;
        data: NonNullable<SummaryState['data']>;
      }>,
    ) {
      const { range, data } = action.payload;
      // Always update the cache for the specific range
      if (!state.cache) {
        // Backward compatibility with persisted state that predates `cache`
        state.cache = {} as SummaryState['cache'];
      }
      state.cache[range] = data;
      // Only update the currently viewed data if the response matches the current selection
      if (state.selectedRange === range) {
        state.data = data;
      }
    },
    clearSummary(state) {
      state.data = null;
      state.error = null;
      state.loading = false;
    },
  },
});

export const {
  setSummaryRange,
  setSummaryLoading,
  setSummaryError,
  setSummaryData,
  clearSummary,
} = summarySlice.actions;

export default summarySlice.reducer;

export type SummarySliceObject = SummaryState;
